import { TierCounter } from '@1x2network/false_persistence'
import { Specification } from '@1x2network/pixi_assets'
import gsap from 'gsap'
import { toSeconds } from '../utils/toSeconds'
import { SoundPlayer } from './SoundPlayer'

const clamp = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max)
}

/**
 * Manages the excitement level of the game
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class Excitement {
    private idleTimer?: GSAPTween
    private winTimer?: GSAPTween
    private soundTiers: number[]
    private spec: ExcitementSpec

    private _hype = 0
    private maxHype = 0
    private minHype = 0
    private state: 'base' | 'holdandwin' = 'base'

    private tierCounter: TierCounter

    constructor(private soundPlayer: SoundPlayer) {
        this.spec = Specification.get('excitement') as ExcitementSpec
        this.tierCounter = new TierCounter({ values: this.spec.excitementThresholds })
        this.soundTiers = this.spec.soundTiers
        this.setState('base')

        this.tierCounter.onTierChange.connect((tier) => {
            this.hype = tier
        })
    }

    private killIdleTimer() {
        this.idleTimer?.kill()
        delete this.idleTimer
    }

    private killWinTimer() {
        this.winTimer?.kill()
        delete this.winTimer
    }

    public setState(state: 'base' | 'holdandwin'): void {
        this.state = state
        this.minHype = this.spec.minHype[state]
        this.maxHype = this.spec.maxHype[state]

        // Constrain the hype to the new min and max values
        this.hype = this._hype

        this.tierCounter.setTier(this._hype)
    }

    public startHype(): void {
        this.minHype = this.spec.minHype[this.state]
        this.maxHype = this.spec.maxHype[this.state]

        if (!this.hype) {
            this.tierCounter.trigger()
        }

        if (this.hype > 1 && !this.winTimer) {
            // Player has clicked to start the game in the niddle of the idle timer reducing the hype. Restart win timer
            this.startWinTimer()
        }

        this.killIdleTimer()
    }

    public startIdle(delay?: number): void {
        // If the player is already idle, don't start a new timer
        if (!this.hype) {
            return
        }

        delay ??= this.spec.idleDecreaseStartDuration

        this.killIdleTimer()
        this.idleTimer = gsap.delayedCall(toSeconds(delay), () => {
            // Hype is being reduced due to player being idle, cancel the win timer
            this.killWinTimer()

            this.reduceHype()
            if (this.hype > this.minHype) {
                this.startIdle(this.spec.idleDecreaseDuration)
            }
        })
    }

    private startWinTimer(): void {
        // After a win, start a timer to lower the hype if the amount of wins isn't matched
        this.killWinTimer()
        this.winTimer = gsap.delayedCall(toSeconds(this.spec.winReduceDuration), () => {
            this.reduceHype()
            if (this.hype > this.minHype) {
                this.startWinTimer()
            }
        })
    }

    public win(): void {
        // Restart the reduce hype timer
        this.startWinTimer()
        this.tierCounter.trigger()
    }

    private reduceHype(): void {
        this.tierCounter.decrementTier()
    }

    public pause(): void {
        this.killWinTimer()
        this.killIdleTimer()
    }

    public reset(): void {
        this.pause()
        this.setState('base')
        this.tierCounter.reset()
        this.idleTimer = undefined
    }

    public setMaxHype() {
        this.hype = this.maxHype
    }

    public set hype(value: number) {
        const oldHype = this._hype
        this._hype = clamp(value, this.minHype, this.maxHype)

        const oldSoundTier = this.soundTiers[oldHype]
        const newSoundTier = this.soundTiers[this._hype]

        // Emit sound tier change
        if (oldSoundTier !== newSoundTier) {
            this.soundPlayer.setTier(newSoundTier)
        }
    }

    public get hype(): number {
        return this._hype
    }
}

export interface ExcitementSpec {
    soundTiers: number[]
    idleDecreaseStartDuration: number
    idleDecreaseDuration: number
    winReduceDuration: number

    minHype: { base: number; holdandwin: number }
    maxHype: { base: number; holdandwin: number }
    excitementThresholds: number[]
}
